import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getGuide } from '../../data/learnContent.js';

/* ─── All content comes from src/data/learnContent.js ─── */
const guide = getGuide('low-back-pain');

const C = {
  navy:    '#0f172a',
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  indigo:  '#4f46e5',
  indigoL: '#6366f1',
  indigoXL:'#eef2ff',
  white:   '#ffffff',
  snow:    '#f8fafc',
  green:   '#059669',
  red:     '#dc2626',
};

const COLOR_MAP = {
  red:    { color: C.red,       bg: '#fef2f2'  },
  amber:  { color: '#d97706',   bg: '#fffbeb'  },
  green:  { color: C.green,     bg: '#f0fdf4'  },
  indigo: { color: C.indigo,    bg: C.indigoXL },
  violet: { color: '#7c3aed',   bg: '#f5f3ff'  },
  cyan:   { color: '#0891b2',   bg: '#ecfeff'  },
};

const resolve = (colorKey) => COLOR_MAP[colorKey] ?? COLOR_MAP.indigo;

/* ─── Mini components ─── */
const Section = ({ title, children }) => (
  <div style={{ marginBottom: 48 }}>
    <h2 style={{ fontSize: 'clamp(1.3rem,2.5vw,1.7rem)', fontWeight: 800, color: C.navy, margin: '0 0 16px', letterSpacing: '-0.02em' }}>{title}</h2>
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ fontSize: '1rem', color: C.muted, lineHeight: 1.8, margin: '0 0 16px' }}>{children}</p>
);

const Tag = ({ children, color = C.indigo, bg = C.indigoXL }) => (
  <span style={{ fontSize: '0.72rem', fontWeight: 800, padding: '3px 10px', borderRadius: 99, background: bg, color, marginRight: 6, display: 'inline-block', marginBottom: 6 }}>{children}</span>
);

const BulletList = ({ items, color = C.indigo }) => (
  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : 'none' }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, marginTop: 7, flexShrink: 0 }} />
        <span style={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.65 }}>{item}</span>
      </li>
    ))}
  </ul>
);

const TestCard = ({ name, method, positive, sensitivity, specificity }) => (
  <div style={{ background: C.white, borderRadius: 14, padding: '18px 20px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>{name}</div>
      <div style={{ display: 'flex', gap: 6 }}>
        {sensitivity && <span style={{ fontSize: '0.65rem', fontWeight: 800, padding: '3px 8px', borderRadius: 99, background: '#d1fae5', color: C.green }}>Sn {sensitivity}</span>}
        {specificity && <span style={{ fontSize: '0.65rem', fontWeight: 800, padding: '3px 8px', borderRadius: 99, background: '#eff6ff', color: C.indigo }}>Sp {specificity}</span>}
      </div>
    </div>
    <div style={{ fontSize: '0.85rem', color: C.muted, lineHeight: 1.6, marginBottom: 8 }}><strong style={{ color: C.ink }}>Method:</strong> {method}</div>
    <div style={{ fontSize: '0.85rem', color: C.red, fontWeight: 600 }}>+ Positive if: {positive}</div>
  </div>
);

const RedFlag = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fca5a5', marginBottom: 8 }}>
    <span style={{ color: C.red, fontSize: '0.9rem', flexShrink: 0 }}>⚠</span>
    <span style={{ fontSize: '0.88rem', color: '#7f1d1d', lineHeight: 1.6 }}>{children}</span>
  </div>
);

/* ─── Page ─── */
export default function LowBackPain() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!guide) return null;

  const { hero, content } = guide;
  const {
    whatIs, keySymptoms, commonCauses, objectiveAssessment,
    specialTests, redFlags, management, cta,
  } = content;

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: C.white, color: C.navy }}>

      {/* ── top nav ── */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}`, padding: '0 clamp(20px,5vw,64px)', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>AssessMonk</span>
        </Link>
        <button onClick={() => navigate('/app')} style={{ padding: '7px 18px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer' }}>
          Try AI Assessment
        </button>
      </nav>

      {/* ── hero (from data) ── */}
      <div style={{ background: `linear-gradient(to bottom, ${C.indigoXL}, ${C.white})`, padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,64px) 0' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {hero.tags.map(t => <Tag key={t.label} color={t.color} bg={t.bg}>{t.label}</Tag>)}
          </div>
          <h1 style={{ fontSize: 'clamp(1.8rem,4vw,2.9rem)', fontWeight: 900, lineHeight: 1.15, letterSpacing: '-0.03em', margin: '0 0 20px' }}>
            {hero.title}<br />
            <span style={{ color: C.muted, fontWeight: 600, fontSize: '0.75em' }}>{hero.subtitle}</span>
          </h1>
          <p style={{ fontSize: '1.05rem', color: C.muted, lineHeight: 1.75, maxWidth: 640, margin: '0 0 32px' }}>{hero.description}</p>
          <div style={{ display: 'flex', gap: 20, padding: '20px 0', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', marginBottom: 48 }}>
            {hero.meta.map(m => (
              <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: C.muted, fontWeight: 600 }}>
                {m}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── main content (from data) ── */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 clamp(20px,5vw,64px) 80px' }}>

        {/* Section 1 — What is it */}
        <Section title={whatIs.title}>
          {whatIs.paragraphs.map((p, i) => <P key={i}>{p}</P>)}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 20 }}>
            {whatIs.classificationCards.map(({ label, desc, colorKey }) => {
              const { color, bg } = resolve(colorKey);
              return (
                <div key={label} style={{ background: bg, borderRadius: 12, padding: '14px 16px', border: `1px solid ${color}33` }}>
                  <div style={{ fontWeight: 800, fontSize: '0.88rem', color }}>{label}</div>
                  <div style={{ fontSize: '0.78rem', color: C.muted, marginTop: 3 }}>{desc}</div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Section 2 — Key symptoms */}
        <Section title={keySymptoms.title}>
          <P>{keySymptoms.intro}</P>
          <BulletList items={keySymptoms.bullets} />
        </Section>

        {/* Section 3 — Common causes */}
        <Section title={commonCauses.title}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {commonCauses.causes.map(c => {
              const { color } = resolve(c.colorKey);
              return (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 12, border: `1px solid ${C.border}`, background: C.snow, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: C.navy }}>{c.label}</div>
                  <div>{c.tags.map(t => <Tag key={t} color={color} bg={`${color}14`}>{t}</Tag>)}</div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Section 4 — Objective assessment */}
        <Section title={objectiveAssessment.title}>
          <P>{objectiveAssessment.intro}</P>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 10, marginBottom: 20 }}>
            {objectiveAssessment.domains.map(d => (
              <div key={d.label} style={{ background: C.white, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: '1.1rem' }}>{d.icon}</span>
                  <span style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy }}>{d.label}</span>
                </div>
                {d.items.map(item => (
                  <div key={item} style={{ fontSize: '0.78rem', color: C.muted, padding: '3px 0', borderTop: `1px solid ${C.border}` }}>• {item}</div>
                ))}
              </div>
            ))}
          </div>
        </Section>

        {/* Section 5 — Special tests */}
        <Section title={specialTests.title}>
          <P>{specialTests.intro}</P>
          {specialTests.tests.map(t => <TestCard key={t.name} {...t} />)}
        </Section>

        {/* Section 6 — Red flags */}
        <Section title={redFlags.title}>
          <P>{redFlags.intro}</P>
          {redFlags.flags.map((f, i) => <RedFlag key={i}>{f}</RedFlag>)}
        </Section>

        {/* Section 7 — Management */}
        <Section title={management.title}>
          <P>{management.intro}</P>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {management.phases.map(p => {
              const { color, bg } = resolve(p.colorKey);
              return (
                <div key={p.phase} style={{ background: bg, borderRadius: 14, padding: '18px 20px', border: `1px solid ${color}33` }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color, marginBottom: 10 }}>{p.phase}</div>
                  <BulletList items={p.items} color={color} />
                </div>
              );
            })}
          </div>
        </Section>

        {/* CTA */}
        <div style={{ background: `linear-gradient(135deg, ${C.indigo}, ${C.indigoL})`, borderRadius: 20, padding: 'clamp(28px,5vw,44px)', textAlign: 'center', marginTop: 48 }}>
          <div style={{ fontSize: '1.6rem', marginBottom: 12 }}>{cta.icon}</div>
          <h3 style={{ fontWeight: 900, fontSize: 'clamp(1.2rem,2.5vw,1.6rem)', color: '#fff', margin: '0 0 12px', letterSpacing: '-0.02em' }}>{cta.heading}</h3>
          <p style={{ color: '#c7d2fe', fontSize: '0.95rem', lineHeight: 1.7, margin: '0 0 28px' }}>{cta.body}</p>
          <button onClick={() => navigate('/app')} style={{
            padding: '13px 32px', borderRadius: 12, fontWeight: 800, fontSize: '0.95rem',
            background: '#fff', color: C.indigo, border: 'none', cursor: 'pointer',
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)', transition: 'all 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}>
            {cta.buttonLabel}
          </button>
        </div>

      </div>
    </div>
  );
}
